import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useContent, useLang } from "@/context/AppContext";
import { EASE } from "@/components/ui-bits";

// Premium Creative Editorial hero — oversized serif display, ivory paper,
// asymmetric magazine grid. Accent words italicised instead of coloured blocks.
const ACCENT_RE = /(growth|terukur|measurable|growth\.)/i;

export default function Hero() {
  const { t, lang } = useLang();
  const { content } = useContent();
  const s = content?.settings || {};
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yPortrait = useTransform(scrollYProgress, [0, 1], [0, 60]);

  const rawHeadline = s.headline?.[lang] || s.headline?.en || "";
  const sub = s.subheadline?.[lang] || s.subheadline?.en || "";
  const whatsapp = s.whatsapp_link || "#contact";

  // Split the headline into serif lines; italicise words carrying the growth motif.
  const words = rawHeadline.split(" ");

  return (
    <section id="top" ref={ref} className="grain-overlay relative overflow-hidden bg-obsidian pb-16 pt-28 sm:pt-36 lg:pb-24">
      {/* faint editorial column rules */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 mx-auto hidden max-w-7xl grid-cols-4 px-8 lg:grid lg:px-16"
      >
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="border-l border-hairline/50 last:border-r" />
        ))}
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        {/* masthead row */}
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE }}
          className="flex flex-wrap items-center justify-between gap-4 border-b border-hairline pb-5"
        >
          <span
            data-testid="hero-availability-badge"
            className="inline-flex items-center gap-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-vermilion"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-pulse-dot rounded-full bg-vermilion" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-vermilion" />
            </span>
            {s.availability?.[lang] || s.availability?.en || ""}
          </span>
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-sand">
            {s.role_title?.[lang] || s.role_title?.en || ""}
          </span>
        </motion.div>

        <div className="grid gap-14 pt-12 sm:pt-16 lg:grid-cols-12 lg:gap-8">
          {/* editorial column: the story */}
          <div className="min-w-0 lg:col-span-8">
            <motion.p
              initial={reduce ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-sand"
            >
              Portfolio — Vol. 01 / {new Date().getFullYear()}
            </motion.p>

            <h1
              className="mt-6 font-display text-[clamp(2.6rem,6.4vw+0.5rem,6rem)] font-medium leading-[1.02] tracking-[-0.02em] text-bone"
              data-testid="hero-headline"
            >
              {words.map((w, i) => {
                const accent = ACCENT_RE.test(w);
                return (
                  <span key={i} className="-mb-2 inline-block overflow-hidden pb-2 align-bottom">
                    <motion.span
                      className={`inline-block ${accent ? "font-light italic text-vermilion" : ""}`}
                      initial={reduce ? false : { y: "112%" }}
                      animate={{ y: 0 }}
                      transition={{ duration: 0.9, delay: 0.18 + i * 0.055, ease: EASE }}
                    >
                      {w}
                      {"\u00A0"}
                    </motion.span>
                  </span>
                );
              })}
            </h1>

            <motion.div
              initial={reduce ? false : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7, ease: EASE }}
              className="mt-10 flex flex-col gap-8 sm:flex-row sm:items-end sm:justify-between"
            >
              <p
                className="max-w-md text-[15px] leading-relaxed text-sand sm:text-base"
                data-testid="hero-subheadline"
              >
                {sub}
              </p>
              <div className="flex shrink-0 flex-wrap items-center gap-3">
                <a
                  href={whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-whatsapp-button"
                  className="group inline-flex items-center gap-2.5 bg-ink px-7 py-4 font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-paper transition-colors hover:bg-vermilion"
                >
                  {t("heroCtaPrimary")}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <a
                  href="#cases"
                  data-testid="hero-explore-case-studies-button"
                  className="inline-flex items-center gap-2.5 border-b border-ink/40 pb-1 font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-bone transition-colors hover:border-vermilion hover:text-vermilion"
                >
                  {t("heroCtaSecondary")}
                  <ArrowDown className="h-4 w-4" />
                </a>
              </div>
            </motion.div>
          </div>

          {/* editorial column: the portrait plate */}
          <div className="min-w-0 lg:col-span-4">
            <motion.figure
              style={{ y: reduce ? 0 : yPortrait }}
              initial={reduce ? false : { opacity: 0, scale: 0.96, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.35, ease: EASE }}
              className="relative mx-auto max-w-xs lg:max-w-none"
              data-testid="hero-portrait-card"
            >
              {/* offset plate frame */}
              <div className="absolute -right-3 -top-3 h-full w-full border border-vermilion/50" aria-hidden="true" />
              <div className="relative border border-hairline bg-surface">
                <img
                  src={s.photo_url || "/images/ryan-portrait.png"}
                  alt="Ryan Hidayat"
                  data-testid="hero-portrait-photo"
                  className="mx-auto h-[380px] w-auto max-w-full object-contain object-bottom sm:h-[460px]"
                />
                <figcaption className="flex items-center justify-between border-t border-hairline px-4 py-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-sand">
                  <span className="text-bone">Ryan Hidayat</span>
                  <span className="text-vermilion">Fig. 01</span>
                </figcaption>
              </div>
            </motion.figure>
          </div>
        </div>
      </div>
    </section>
  );
}
